import { useEffect, useState } from 'react';
import { useGlobalSearchDrillDown } from '../../../services/modules/global-search/globalSearch.queries';
import { INITIAL_PAGE_SIZE } from '../globalSearch.constants';
import { SearchResultGroup, SearchResultItem } from '../globalSearch.types';

interface UseGlobalSearchPaginationParams {
  entityType: SearchResultGroup['entityType'] | null;
  query: string;
  overviewGroup?: SearchResultGroup;
  isEnabled: boolean;
}

const useGlobalSearchPagination = ({
  entityType,
  query,
  overviewGroup,
  isEnabled,
}: UseGlobalSearchPaginationParams) => {
  const [isDrillEnabled, setIsDrillEnabled] = useState(false);

  useEffect(() => {
    setIsDrillEnabled(false);
  }, [entityType, query]);

  const drillEntityType = entityType ?? 'product';

  const drillDown = useGlobalSearchDrillDown(
    {
      q: query,
      entityType: drillEntityType,
      startSkip: INITIAL_PAGE_SIZE,
    },
    { enabled: isEnabled && Boolean(entityType) && isDrillEnabled },
  );

  const seedItems = overviewGroup?.items ?? [];
  const drillItems: SearchResultItem[] =
    drillDown.data?.pages.flatMap(
      (page) => page.groups.find((group) => group.entityType === drillEntityType)?.items ?? [],
    ) ?? [];
  const seenSeedIds = new Set(seedItems.map((item) => item.id));
  const items = [...seedItems, ...drillItems.filter((item) => !seenSeedIds.has(item.id))];

  const hasDrillPages = (drillDown.data?.pages.length ?? 0) > 0;
  const hasMore =
    isEnabled && (hasDrillPages ? Boolean(drillDown.hasNextPage) : Boolean(overviewGroup?.hasMore));
  const isLoadingMore = drillDown.isFetching;

  const loadMore = () => {
    if (!isEnabled || drillDown.isFetching) {
      return;
    }
    if (!isDrillEnabled) {
      setIsDrillEnabled(true);
    }
    if (drillDown.hasNextPage) {
      drillDown.fetchNextPage();
    }
  };

  return { items, hasMore, isLoadingMore, loadMore };
};

export default useGlobalSearchPagination;
